// Filters component for soft tag list filtering and search
import React, { useEffect, useState } from 'react';
import { Button, Select, Input } from '@yaradigitallabs/ahua-react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  selectSearchFilters,
  selectTagsFilters,
  selectTagsLoading,
} from '../store/tags.selectors';
import type { FilterOption, TagsSearchFilters } from '../types/tags.types';
import { getSoftTags } from '../store/tags.thunk';
import { clearSearchFilters, setSearchFilter } from '../store/tags.slice';

// Props for Filters component
// onActiveFiltersChange: callback to notify parent when filters are active
type FiltersProps = {
  onActiveFiltersChange?: (hasActiveFilters: boolean) => void;
};

// Filters renders dropdowns and search input for filtering soft tags
export const Filters = ({ onActiveFiltersChange }: FiltersProps) => {
  const dispatch = useAppDispatch();
  const searchFilters = useAppSelector(selectSearchFilters);
  const [filters, setFilters] = useState<TagsSearchFilters>(searchFilters);
  const [hasActiveFilters, setHasActiveFilters] = useState<boolean>(false);
  const filterOptions = useAppSelector(selectTagsFilters);
  const productionSitesOptions: FilterOption[] = filterOptions.productionSites;
  const areasOptions: FilterOption[] = filterOptions.areas;
  const productionUnitsOptions: FilterOption[] = filterOptions.productionUnits;
  const loading = useAppSelector(selectTagsLoading);

  // Handles search button click, dispatches search action
  const handleTagSearch = () => {
    if (
      (filters.search_term !== '' && filters.search_term.length >= 3) ||
      filters.search_term === ''
    ) {
      const payload = {
        search_term: filters.search_term,
        site: filters.site,
        area: filters.area,
        production_unit: filters.production_unit,
      };
      dispatch(setSearchFilter(payload));
      dispatch(getSoftTags(payload));
    }
  };

  // Handles filter dropdown/input changes
  const handleFilterChange = (
    filterType: keyof TagsSearchFilters,
    selectedValues: string
  ) => {
    const updatedFilters = {
      ...filters,
      [filterType]: selectedValues,
    };
    setFilters(updatedFilters);
  };

  // Handles reset button click, clears filters and reloads tags
  const handleReset = () => {
    const resetFilters: TagsSearchFilters = {
      search_term: '',
      site: '',
      area: '',
      production_unit: '',
    };
    setFilters(resetFilters);
    dispatch(clearSearchFilters());
    dispatch(getSoftTags());
  };

  useEffect(() => {
    const searchTagFilterCondition =
      filters.search_term !== '' && filters.search_term.length >= 3;
    const searchTagFilterEmptyCondition = filters.search_term === '';
    const dropdownFilterCondition =
      filters.site !== '' ||
      filters.area !== '' ||
      filters.production_unit !== '';

    if (searchTagFilterCondition) {
      setHasActiveFilters(true);
    } else if (searchTagFilterEmptyCondition) {
      if (dropdownFilterCondition) setHasActiveFilters(true);
      else setHasActiveFilters(false);
    } else {
      setHasActiveFilters(false);
    }
  }, [filters]);

  // Notify parent when active filter state changes
  React.useEffect(() => {
    onActiveFiltersChange?.(hasActiveFilters);
  }, [hasActiveFilters, onActiveFiltersChange]);

  return (
    <div className="px-4 py-3 items-center">
      {/* Filter Controls */}

      {/* Dropdowns Row */}
      <div className="flex justify-between">
        <div className="flex gap-4 w-[80%]">
          {/* Search input for soft tag ID or name */}
          <Input
            iconTrailing="Search"
            label="Search Soft tag name"
            helperText="e.g.: SAFIC208"
            size={'xs'}
            css={{
              width: '300px',
            }}
            value={filters.search_term}
            onChange={e =>
              handleFilterChange('search_term', e.target.value as string)
            }
            readOnly={loading}
            cover="fill"
          />

          {/* Production Site dropdown */}
          <Select
            ariaLabel="Select Production Site"
            cover="fill"
            items={productionSitesOptions}
            loadingText="Loading..."
            placeholder="Production sites"
            position="popper"
            selectContentAutoHeight
            size="xs"
            variant="default"
            value={filters.site}
            css={{
              width: '200px',
            }}
            onChange={values => handleFilterChange('site', values as string)}
            disabled={productionSitesOptions.length === 0 || loading}
          />
          {/* Area dropdown */}
          <Select
            ariaLabel="Select Area"
            cover="fill"
            items={areasOptions}
            loadingText="Loading..."
            placeholder="Areas"
            position="popper"
            selectContentAutoHeight
            size="xs"
            variant="default"
            value={filters.area}
            css={{
              width: '200px',
            }}
            onChange={values => handleFilterChange('area', values as string)}
            disabled={areasOptions.length === 0 || loading}
          />
          {/* Production Unit dropdown */}
          <Select
            ariaLabel="Select Production Unit"
            cover="fill"
            items={productionUnitsOptions}
            loadingText="Loading..."
            placeholder="Production units"
            position="popper"
            selectContentAutoHeight
            size="xs"
            variant="default"
            value={filters.production_unit}
            css={{
              width: '200px',
            }}
            onChange={values =>
              handleFilterChange('production_unit', values as string)
            }
            disabled={productionUnitsOptions.length === 0 || loading}
          />
          <div className="flex gap-0.5 w-[20%]">
            {/* Reset Button */}
            <Button
              variant="ghost"
              size="s"
              onClick={handleReset}
              colorConcept="brand"
              disabled={!hasActiveFilters || loading}
            >
              Reset
            </Button>

            {/* Search Button */}
            <Button
              variant="outline"
              size="s"
              onClick={handleTagSearch}
              colorConcept="brand"
              disabled={!hasActiveFilters || loading}
            >
              Search
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
